import {Paper, Table, TableBody, TableContainer} from '@mui/material';
import React from 'react';
import TeamSessionListItem from './TeamSessionListItem';
import {cloneDeep} from 'lodash';
import {
  getTeamBaselineAverageEnergyRatio,
  getTeamSessionEnergyRatio,
} from '../../services/teamService';
import {PlayerSession, TeamSession} from '../../interfaces';
import {Positions} from '../../enums';
import {
  getEnergyRatioDifference,
  getEnergyRatioFromFloat,
} from '../../services/measurementService';
import {useTranslation} from 'react-i18next';

interface Props {
  teamSessions: any[];
  teamSize?: number;
  isTeamSessionGroup?: boolean;
}

const TeamSessionsList: React.FC<Props> = ({
  teamSessions,
  teamSize,
  isTeamSessionGroup,
}) => {
  const {t} = useTranslation();
  let combinedTeamSessions: any[] = [];

  if (!isTeamSessionGroup) {
    const teamSessionGroupsObject = teamSessions
      .filter(teamSession => teamSession.teamSessionGroup)
      .reduce((acc, teamSession) => {
        const key = teamSession.teamSessionGroup.id.toString();
        (acc[key] = acc[key] || []).push(teamSession);
        return acc;
      }, {});

    const teamSessionGroups = Object.keys(teamSessionGroupsObject).map(
      teamSessionGroupKey => {
        const teamSessionGroup = teamSessionGroupsObject[teamSessionGroupKey];
        const allPlayerSessions = teamSessionGroup.flatMap(
          (ts: TeamSession) => ts.playerSessions ?? [],
        );
        const defensePlayerSessions = (allPlayerSessions || []).filter(
          (ps: PlayerSession) =>
            ps.player?.profile?.position === Positions.defense,
        );
        const forwardPlayerSessions = (allPlayerSessions || []).filter(
          (ps: PlayerSession) =>
            [
              Positions.center,
              Positions.leftwing,
              Positions.rightwing,
            ].includes(ps.player?.profile?.position as Positions),
        );

        const orderedTeamSessions = teamSessionGroup.sort((a: any, b: any) => {
          // Turn your strings into dates, and then subtract them
          // to get a value that is either negative, positive, or zero.
          return (
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
          );
        });

        const playerIds = orderedTeamSessions
          .map((teamSession: any) =>
            teamSession.playerSessions.map(
              (playerSession: any) => playerSession?.player?.id,
            ),
          )
          .reduce((a: any, b: any) => a.concat(b), []);

        const uniquePlayerIds = [...new Set(playerIds)];

        // return a new team session
        return {
          startTime: orderedTeamSessions[0].startTime,
          localDisplayStartTime: orderedTeamSessions[0].localDisplayStartTime,
          timeZone: orderedTeamSessions[0].timeZone,
          endTime: [...orderedTeamSessions].reverse()[0].endTime,
          type: orderedTeamSessions[0].type,
          participation: uniquePlayerIds.length,
          teamId: orderedTeamSessions[0].teamId,
          teamSize: orderedTeamSessions[0].teamSize ?? teamSize,
          teamSessionGroupId: orderedTeamSessions[0].teamSessionGroup.id,
          energyRatioForward: getTeamSessionEnergyRatio(
            forwardPlayerSessions ?? [],
          ),
          energyRatioForwardBaseline: getTeamBaselineAverageEnergyRatio(
            (forwardPlayerSessions ?? []).map(
              (fps: PlayerSession) => fps.baseline,
            ),
          ),
          energyRatioDefense: getTeamSessionEnergyRatio(
            defensePlayerSessions ?? [],
          ),
          energyRatioDefenseBaseline: getTeamBaselineAverageEnergyRatio(
            (defensePlayerSessions ?? []).map(
              (dps: PlayerSession) => dps.baseline,
            ),
          ),
          hasVideo: orderedTeamSessions.some((ts: TeamSession) => ts.hasVideo),
          hasPendingPlayerSessions: orderedTeamSessions.some(
            (ts: TeamSession) => ts.hasPendingPlayerSessions,
          ),
          // get the last team session from orderedTeamSessions
          sessionDetail:
            orderedTeamSessions[orderedTeamSessions.length - 1].sessionDetail,
        };
      },
    );

    const teamSessionIndividuals = cloneDeep(teamSessions)
      .filter(teamSession => !teamSession.teamSessionGroup)
      .map(teamSession => {
        const defensePlayerSessions = (teamSession.playerSessions || []).filter(
          (ps: PlayerSession) => ps.position === Positions.defense,
        );
        const forwardPlayerSessions = (teamSession.playerSessions || []).filter(
          (ps: PlayerSession) =>
            [Positions.center, Positions.leftwing, Positions.rightwing].indexOf(
              ps.position as Positions,
            ) !== -1,
        );
        teamSession.participation = (teamSession.playerSessions ?? []).length;
        teamSession.energyRatioForward = getTeamSessionEnergyRatio(
          forwardPlayerSessions ?? [],
        );
        teamSession.energyRatioForwardBaseline =
          getTeamBaselineAverageEnergyRatio(
            (forwardPlayerSessions ?? []).map(
              (fps: PlayerSession) => fps.baseline,
            ),
          );
        teamSession.energyRatioDefense = getTeamSessionEnergyRatio(
          defensePlayerSessions ?? [],
        );
        teamSession.energyRatioDefenseBaseline =
          getTeamBaselineAverageEnergyRatio(
            (defensePlayerSessions ?? []).map(
              (dps: PlayerSession) => dps.baseline,
            ),
          );
        return teamSession;
      });

    combinedTeamSessions = [...teamSessionGroups, ...teamSessionIndividuals];
  } else {
    combinedTeamSessions = cloneDeep(teamSessions).map(teamSession => {
      const defensePlayerSessions = (teamSession.playerSessions || []).filter(
        (ps: PlayerSession) =>
          ps.player?.profile?.position === Positions.defense,
      );
      const forwardPlayerSessions = (teamSession.playerSessions || []).filter(
        (ps: PlayerSession) =>
          [Positions.center, Positions.leftwing, Positions.rightwing].includes(
            ps.player?.profile?.position as Positions,
          ),
      );
      teamSession.participation = (teamSession.playerSessions ?? []).length;
      teamSession.energyRatioForward = getTeamSessionEnergyRatio(
        forwardPlayerSessions ?? [],
      );
      teamSession.energyRatioForwardBaseline =
        getTeamBaselineAverageEnergyRatio(
          (forwardPlayerSessions ?? []).map(
            (fps: PlayerSession) => fps.baseline,
          ),
        );
      teamSession.energyRatioDefense = getTeamSessionEnergyRatio(
        defensePlayerSessions ?? [],
      );
      teamSession.energyRatioDefenseBaseline =
        getTeamBaselineAverageEnergyRatio(
          (defensePlayerSessions ?? []).map(
            (dps: PlayerSession) => dps.baseline,
          ),
        );
      return teamSession;
    });
  }

  return (
    <TableContainer sx={{mb: 1}} component={Paper}>
      <Table aria-label={t('ariaLabels.tables.teamSessionsList') as string}>
        <TableBody>
          {[...(combinedTeamSessions || [])]
            .sort((a: any, b: any) => {
              // Turn your strings into dates, and then subtract them
              // to get a value that is either negative, positive, or zero.
              if (isTeamSessionGroup) {
                return (
                  new Date(a.startTime).getTime() -
                  new Date(b.startTime).getTime()
                );
              } else {
                return (
                  new Date(b.startTime).getTime() -
                  new Date(a.startTime).getTime()
                );
              }
            })
            .map((teamSession: any) => (
              <TeamSessionListItem
                key={
                  teamSession.teamSessionGroupId && !isTeamSessionGroup
                    ? `teamSessionGroup-${teamSession.teamSessionGroupId}`
                    : teamSession.id
                }
                startTime={new Date(teamSession.startTime)}
                endTime={new Date(teamSession.endTime)}
                sessionDetail={teamSession.sessionDetail}
                type={teamSession.type}
                participation={teamSession.participation}
                sessionTags={teamSession.sessionTags}
                teamSize={teamSession.teamSize ?? teamSize}
                detailLink={
                  teamSession.teamSessionGroupId && !isTeamSessionGroup
                    ? `/teams/${teamSession.teamId}/teamsessiongroups/${teamSession.teamSessionGroupId}`
                    : `/teams/${teamSession.teamId}/teamsessions/${teamSession.id}`
                }
                energyRatioForward={
                  teamSession.energyRatioForward
                    ? getEnergyRatioFromFloat(teamSession.energyRatioForward)
                    : undefined
                }
                energyRatioForwardBaseline={getEnergyRatioDifference(
                  teamSession.energyRatioForward,
                  teamSession.energyRatioForwardBaseline,
                )}
                energyRatioDefense={
                  teamSession.energyRatioDefense
                    ? getEnergyRatioFromFloat(teamSession.energyRatioDefense)
                    : undefined
                }
                energyRatioDefenseBaseline={getEnergyRatioDifference(
                  teamSession.energyRatioDefense,
                  teamSession.energyRatioDefenseBaseline,
                )}
                localDisplayStartTime={teamSession.localDisplayStartTime}
                hasVideo={teamSession.hasVideo}
                hasPendingPlayerSessions={teamSession.hasPendingPlayerSessions}
                timezone={teamSession.timeZone}
              />
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TeamSessionsList;
